import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../../redux";
import { UserInfo } from "../../../ui/models/userInfo";
import { ProfesionalPerfil } from "../../models/profesional-perfil";
import { profesionalServices } from "../../services/profesional.services";
import { localidades } from "../../utils/localidades";
import "./perfil.scss";

const AREAS_OPTIONS = [
  "Audición",
  "Vestibular",
  "Comunicación y lenguaje",
  "Habla",
  "Neurorehabilitación",
  "Voz",
  "Motricidad orofacial",
];
const POBLACION_OPTIONS = [
  "Recién nacidos",
  "Niños/as",
  "Adolescentes",
  "Adultos",
  "Adultos mayores",
];
const REDES_CONFIG = [
  {
    key: "facebook",
    label: "Facebook",
    placeholder: "https://facebook.com/tu-perfil",
    icon: "bi-facebook",
    color: "#3b5998",
  },
  {
    key: "instagram",
    label: "Instagram",
    placeholder: "https://instagram.com/tu-perfil",
    icon: "bi-instagram",
    color: "#e4405f",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    placeholder: "https://linkedin.com/in/tu-perfil",
    icon: "bi-linkedin",
    color: "#0077b5",
  },
  {
    key: "x.com",
    label: "X (Twitter)",
    placeholder: "https://x.com/tu-usuario",
    icon: "bi-twitter-x",
    color: "#000",
  },
] as const;

type RedKey = (typeof REDES_CONFIG)[number]["key"];

function getRedUrl(redes: string[], key: RedKey): string {
  return redes.find((u) => u.toLowerCase().includes(key)) ?? "";
}

function buildRedes(redUrls: Record<RedKey, string>): string[] {
  return Object.values(redUrls).filter((u) => u.trim() !== "");
}

function ChipGroup({
  label,
  options,
  selected,
  onChange,
  color,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (val: string[]) => void;
  color: "cyan" | "green" | "violet" | "amber";
}) {
  const toggle = (opt: string) =>
    onChange(
      selected.includes(opt)
        ? selected.filter((s) => s !== opt)
        : [...selected, opt]
    );

  return (
    <div className={`perfil-chip-group perfil-chip-group--${color}`}>
      <span className="perfil-chip-group__label">{label}</span>
      <div className="perfil-chip-group__chips">
        {options.map((opt) => (
          <Chip
            key={opt}
            label={opt}
            onClick={() => toggle(opt)}
            variant={selected.includes(opt) ? "filled" : "outlined"}
            className={`perfil-chip ${selected.includes(opt) ? "perfil-chip--active" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Perfil() {
  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  const [perfil, setPerfil] = useState<ProfesionalPerfil | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    open: boolean;
    success: boolean;
    msg: string;
  }>({
    open: false,
    success: true,
    msg: "",
  });

  const [domicilioLaboral, setDomicilioLaboral] = useState("");
  const [localidadLaboral, setLocalidadLaboral] = useState("");
  const provinciaLaboral = "La Pampa";
  const [atiendePorObraSocial, setAtiendePorObraSocial] = useState(false);
  const [areas, setAreas] = useState<string[]>([]);
  const [poblacion, setPoblacion] = useState<string[]>([]);

  // URLs individuales por red
  const [redUrls, setRedUrls] = useState<Record<RedKey, string>>({
    facebook: "",
    instagram: "",
    linkedin: "",
    "x.com": "",
  });
  const setRedUrl = (key: RedKey, val: string) =>
    setRedUrls((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    if (!authInfo.profesionalId) {
      setError("No se encontró un profesional asociado a este usuario.");
      setLoading(false);
      return;
    }
    profesionalServices
      .getPerfil(authInfo.profesionalId)
      .then(({ data }) => {
        setPerfil(data);
        setDomicilioLaboral(data.domicilioLaboral ?? "");
        setLocalidadLaboral(data.localidadLaboral ?? "");
        setAtiendePorObraSocial(data.atiendePorObraSocial ?? false);
        setAreas(data.areas ?? []);
        setPoblacion(data.poblacion ?? []);
        setRedUrls({
          facebook: getRedUrl(data.redes ?? [], "facebook"),
          instagram: getRedUrl(data.redes ?? [], "instagram"),
          linkedin: getRedUrl(data.redes ?? [], "linkedin"),
          "x.com": getRedUrl(data.redes ?? [], "x.com"),
        });
      })
      .catch(() => setError("Error al cargar el perfil. Intente nuevamente."))
      .finally(() => setLoading(false));
  }, [authInfo.profesionalId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authInfo.profesionalId) return;
    setSaving(true);
    try {
      await profesionalServices.updatePerfil(authInfo.profesionalId, {
        domicilioLaboral,
        localidadLaboral,
        provinciaLaboral,
        atiendePorObraSocial,
        areas,
        redes: buildRedes(redUrls),
        poblacion,
      });
      setToast({
        open: true,
        success: true,
        msg: "Perfil actualizado correctamente.",
      });
    } catch {
      setToast({
        open: true,
        success: false,
        msg: "Error al guardar los cambios.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="perfil-state">
        <CircularProgress size={40} sx={{ color: "#06b6d4" }} />
        <span>Cargando perfil...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="perfil-state perfil-state--error">
        <ErrorOutlineIcon />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="perfil-container">
      <div className="perfil-header">
        <div className="perfil-header__avatar">
          {perfil!.nombre[0]}
          {perfil!.apellido[0]}
        </div>
        <div className="perfil-header__info">
          <Typography variant="h5" className="perfil-header__name">
            {perfil!.nombre} {perfil!.apellido}
          </Typography>
          <div className="perfil-header__badges">
            <span className="perfil-badge perfil-badge--cyan">
              <BadgeOutlinedIcon fontSize="small" />
              Mat. {perfil!.matriculaProf}
            </span>
            <span className="perfil-badge perfil-badge--slate">
              <AccountCircleOutlinedIcon fontSize="small" />
              {authInfo.userName}
            </span>
            {authInfo.email && (
              <span className="perfil-badge perfil-badge--slate">
                {authInfo.email}
              </span>
            )}
          </div>
        </div>
      </div>

      <form className="perfil-form" onSubmit={handleSubmit}>
        <div className="perfil-section">
          <div className="perfil-section__title">
            <HomeWorkOutlinedIcon />
            <span>Domicilio Laboral</span>
          </div>
          <div className="perfil-section__body">
            <div className="perfil-field-group">
              <label className="perfil-label">Domicilio</label>
              <input
                className="perfil-input"
                type="text"
                value={domicilioLaboral}
                onChange={(e) => setDomicilioLaboral(e.target.value)}
                placeholder="Ej: Av. San Martín 1234"
                maxLength={200}
              />
            </div>
            <div className="perfil-field-row">
              <div className="perfil-field-group">
                <label className="perfil-label">Localidad</label>
                <select
                  className="perfil-input perfil-select"
                  value={localidadLaboral}
                  onChange={(e) => setLocalidadLaboral(e.target.value)}
                >
                  <option value="">Seleccione una localidad</option>
                  {localidades.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>
              <div className="perfil-field-group">
                <label className="perfil-label">Provincia</label>
                <input
                  className="perfil-input"
                  type="text"
                  value={provinciaLaboral}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <div className="perfil-section">
          <div className="perfil-section__title">
            <PeopleAltOutlinedIcon />
            <span>Atención</span>
          </div>
          <div className="perfil-section__body">
            <label className="perfil-toggle">
              <input
                type="checkbox"
                checked={atiendePorObraSocial}
                onChange={(e) => setAtiendePorObraSocial(e.target.checked)}
              />
              <span className="perfil-toggle__track" />
              <span className="perfil-toggle__label">
                Atiende por Obra Social
              </span>
            </label>
            <ChipGroup
              label="Áreas de especialización"
              options={AREAS_OPTIONS}
              selected={areas}
              onChange={setAreas}
              color="cyan"
            />
            <ChipGroup
              label="Población que atiende"
              options={POBLACION_OPTIONS}
              selected={poblacion}
              onChange={setPoblacion}
              color="green"
            />
          </div>
        </div>

        <div className="perfil-section">
          <div className="perfil-section__title">
            <ShareOutlinedIcon />
            <span>Redes y Contacto</span>
          </div>
          <div className="perfil-section__body">
            <div className="perfil-field-row perfil-field-row--redes">
              {REDES_CONFIG.map(({ key, label, placeholder, icon, color }) => (
                <div className="perfil-field-group" key={key}>
                  <label className="perfil-label perfil-label--red">
                    <i className={`bi ${icon}`} style={{ color }} />
                    {label}
                  </label>
                  <input
                    className="perfil-input"
                    type="url"
                    value={redUrls[key]}
                    onChange={(e) => setRedUrl(key, e.target.value)}
                    placeholder={placeholder}
                    maxLength={300}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="perfil-actions">
          <button
            className="perfil-btn perfil-btn--save"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" />
            )}
            {saving ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>
      </form>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.success ? "success" : "error"}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}
